export type Language = 'tr' | 'en';

export interface Translations {
  // Common
  loading: string;
  cancel: string;
  save: string;
  delete: string;
  edit: string;
  add: string;
  yes: string;
  no: string;
  confirm: string;
  select: string;
  taglines: string[];

  // Auth
  auth: {
    signIn: string;
    signUp: string;
    signOut: string;
    email: string;
    password: string;
    confirmPassword: string;
    name: string;
    enterEmail: string;
    enterPassword: string;
    confirmYourPassword: string;
    enterName: string;
    signInToAccount: string;
    createAccount: string;
    noAccount: string;
    hasAccount: string;
    pleaseWait: string;
    nameRequired: string;
    passwordsDoNotMatch: string;
    passwordRequirements: string;
    passwordMinLength: string;
    passwordUppercase: string;
    passwordLowercase: string;
    passwordNumber: string;
    passwordSymbol: string;
    // Forgot password
    forgotPassword: string;
    forgotPasswordTitle: string;
    forgotPasswordSubtitle: string;
    sendResetLink: string;
    backToLogin: string;
    resetLinkSent: string;
    // Reset password
    resetPassword: string;
    resetPasswordTitle: string;
    newPassword: string;
    enterNewPassword: string;
    passwordResetSuccess: string;
    // Email verification
    verifyEmail: string;
    verifyEmailTitle: string;
    verifyingEmail: string;
    emailVerified: string;
    emailVerificationFailed: string;
    verificationEmailSent: string;
    checkYourEmail: string;
    termsNotice: string;
    // Policy links
    termsOfUse: string;
    privacyPolicy: string;
    cookiePolicy: string;
    softwareDisclaimer: string;
    termsOfUseTitle: string;
    privacyPolicyTitle: string;
    cookiePolicyTitle: string;
    acceptAndRegister: string;
  };

  // Legal policies content
  policies: {
    terms: string;
    privacy: string;
    cookies: string;
  };

  // Navigation
  nav: {
    dashboard: string;
    logout: string;
    help: string;
    about: string;
    pricing: string;
  };

  // Dashboard
  dashboard: {
    title: string;
    products: string;
  };

  // Emirler (Orders/Commands)
  emirler: {
    title: string;
    addOrder: string;
    noEmirlerMatch: string;
    platform: string;
    product: string;
    sku: string;
    priceRange: string;
    minPrice: string;
    maxPrice: string;
    orderType: string;
    status: string;
    buyboxCapture: string;
    pressureApply: string;
    working: string;
    waiting: string;
    inactive: string;
    deactivationReason: string;
    actions: string;
    delete: string;
    restart: string;
    stop: string;
    editOrder: string;
    orderUpdated: string;
    orderUpdateError: string;
    orderRestarted: string;
    orderRestartError: string;
    orderStopped: string;
    orderStopError: string;
  };

  // API Keys
  apiKeys: {
    title: string;
    addKey: string;
    editKey: string;
    updateKey: string;
    version: string;
    selectVersion: string;
    apiKey: string;
    noKeys: string;
    addFirstKey: string;
    daysLeft: string;
    expired: string;
    active: string;
    expires: string;
    added: string;
    deleteConfirm: string;
    keyPlaceholder: string;
    validFor: string;
    days: string;
    user: string;
    sellerId: string;
    platform: string;
    type: string;
  };

  // Filters
  filters: {
    all: string;
    active: string;
    buyboxWin: string;
    pressureWinner: string;
    deactivated: string;
    notActivated: string;
  };

  // Table
  table: {
    noData: string;
    noProductsMatch: string;
  };

  // Column headers (auto-generated can be overridden)
  columns: {
    id: string;
    status: string;
    name: string;
    sku: string;
    price: string;
    stock: string;
    deactivationReason: string;
  };

  // Footer
  footer: {
    aPolysanProduct: string;
    copyright: string;
    companyName: string;
    companyType: string;
    address: string;
    phone: string;
    email: string;
    privacyPolicy: string;
    termsOfService: string;
    returnPolicy: string;
    cookiePolicy: string;
    dataDeletion: string;
    instantDelivery: string;
  };

  // Add Key Page
  addKey: {
    backToDashboard: string;
    title: string;
    subtitle: string;
    installTitle: string;
    dragInstruction: string;
    bookmarkletName: string;
    altInstruction: string;
    howToInstall: string;
    step1Title: string;
    step2: string;
    step3: string;
    step4: string;
    howToUse: string;
    useStep1: string;
    useStep2: string;
    useStep3: string;
    useStep4: string;
    supportedSites: string;
    statusActive: string;
    statusComingSoon: string;
    importantNotice: string;
    removeBookmarklet: string;
    mobileNotice: string;
    mobileNoticeDesc: string;
  };

  // Add Orders Page
  addOrders: {
    backToDashboard: string;
    title: string;
    subtitle: string;
    selectPlatform: string;
    selectPlatformDesc: string;
    comingSoon: string;
    searchProducts: string;
    searchPlaceholder: string;
    noProducts: string;
    noProductsDesc: string;
    loadingProducts: string;
    addOrder: string;
    // Add Order Form
    addOrderTitle: string;
    productInfo: string;
    priceSettings: string;
    currency: string;
    selectCurrency: string;
    minPrice: string;
    maxPrice: string;
    orderType: string;
    selectOrderType: string;
    buyboxCapture: string;
    pressureApply: string;
    priceIncludesCommission: string;
    activateCoupon: string;
    activatePromotion: string;
    pricingHint: string;
    // Validation
    minPriceRequired: string;
    maxPriceRequired: string;
    minPriceMustBePositive: string;
    maxPriceMustBePositive: string;
    minPriceGreaterThanMax: string;
    currencyRequired: string;
    orderTypeRequired: string;
    // Currencies
    currencyTRY: string;
    currencyUSD: string;
    currencyEUR: string;
    currencyYUAN: string;
    // Success/Error
    orderAddedSuccess: string;
    orderAddedError: string;
    // Table columns
    productName: string;
    sku: string;
    currentPrice: string;
    stock: string;
    actions: string;
    // Pagination
    firstPage: string;
    prevPage: string;
    nextPage: string;
    lastPage: string;
    products: string;
  };

  // Bulk Upload
  bulkUpload: {
    title: string;
    buttonText: string;
    downloadTemplate: string;
    uploadFile: string;
    templateFilename: string;
    dropzone: string;
    dropzoneActive: string;
    selectFile: string;
    selectedFile: string;
    uploadAndValidate: string;
    validating: string;
    processing: string;
    resultsTitle: string;
    successCount: string;
    errorCount: string;
    createdCount: string;
    updatedCount: string;
    unchangedCount: string;
    actionCreated: string;
    actionUpdated: string;
    actionUnchanged: string;
    downloadErrors: string;
    close: string;
    tryAgain: string;
    noFile: string;
    invalidFormat: string;
    emptyFile: string;
    missingColumns: string;
    fileTooLarge: string;
    suspiciousContent: string;
    // Template columns
    columnDescription: string;
    columnBarcode: string;
    columnMinPrice: string;
    columnMaxPrice: string;
    columnCurrency: string;
    columnOrderType: string;
    columnCommissionIncluded: string;
    columnCouponActive: string;
    columnPromotionActive: string;
    // Error messages
    errors: {
      MISSING_BARCODE: string;
      INVALID_PRICE: string;
      NEGATIVE_PRICE: string;
      TIER_LIMIT_REACHED: string;
      DUPLICATE_ORDER: string;
      DUPLICATE_IN_BATCH: string;
      PRODUCT_NOT_FOUND: string;
      PRODUCT_NOT_ON_SALE: string;
      PRODUCT_ARCHIVED: string;
      PRICE_TOO_LOW: string;
      PRICE_TOO_HIGH: string;
      API_AUTH_FAILED: string;
      API_ACCESS_DENIED: string;
      API_RATE_LIMITED: string;
      INVALID_BARCODE_FORMAT: string;
      API_SERVER_ERROR: string;
      API_ERROR: string;
      API_CONNECTION_ERROR: string;
    };
  };

  // Payment Plans
  paymentPlans: {
    // Tooltip
    tooltipText: string;
    // Page
    title: string;
    subtitle: string;
    // Billing toggle
    monthly: string;
    annual: string;
    annualDiscount: string;
    // Tier names
    demo: string;
    tier1: string;
    tier2: string;
    tier3: string;
    tier4: string;
    tier5: string;
    tier6: string;
    tier7: string;
    tier8: string;
    tier9: string;
    tier10: string;
    tier11: string;
    // Pricing
    free: string;
    perMonth: string;
    perYear: string;
    tryPerMonth: string;
    // Emir counts
    emirCount: string;
    emirs: string;
    // Tier descriptions
    demoDesc: string;
    tier1Desc: string;
    tier2Desc: string;
    tier3Desc: string;
    tier4Desc: string;
    tier5Desc: string;
    tier6Desc: string;
    tier7Desc: string;
    tier8Desc: string;
    tier9Desc: string;
    tier10Desc: string;
    tier11Desc: string;
    // Badges
    recommended: string;
    currentPlan: string;
    trialPeriod: string;
    // Features
    features: string;
    allPlatforms: string;
    buyboxCapture: string;
    pressureMode: string;
    prioritySupport: string;
    apiAccess: string;
    // Buttons
    buyMonthly: string;
    buyAnnual: string;
    contactSales: string;
    currentlyActive: string;
    renewMonthly: string;
    renewAnnual: string;
    // Info sections
    whatIsEmir: string;
    whatIsEmirDesc: string;
    kdvIncluded: string;
    demoTrialNote: string;
    annualBenefits: string;
    annualBenefitsDesc: string;
    // Sales pitches
    pitch1: string;
    pitch2: string;
    pitch3: string;
    // FAQ
    faqTitle: string;
    faq1Q: string;
    faq1A: string;
    faq2Q: string;
    faq2A: string;
    faq3Q: string;
    faq3A: string;
    // Downgrade
    showDowngrades: string;
    hideDowngrades: string;
    downgrade: string;
    // Days left
    daysLeft: string;
  };

  // Help Page
  help: {
    title: string;
    subtitle: string;
    downloadPdf: string;
    backToDashboard: string;
    // Tabs
    tabs: {
      gettingStarted: string;
      apiKeys: string;
      orders: string;
      orderDetails: string;
      contact: string;
    };
    // Getting Started Section
    gettingStarted: {
      whatIsPolaris: string;
      whatIsPolarisDesc: string;
      supportedPlatforms: string;
      supportedPlatformsDesc: string;
      createAccount: string;
      createAccountStep1: string;
      createAccountStep2: string;
      createAccountStep3: string;
      createAccountStep4: string;
      passwordRequirements: string;
      passwordReq1: string;
      passwordReq2: string;
      passwordReq3: string;
      passwordReq4: string;
      passwordReq5: string;
      emailVerification: string;
      emailVerificationDesc: string;
      login: string;
      loginDesc: string;
      forgotPassword: string;
      forgotPasswordDesc: string;
    };
    // API Keys Section
    apiKeys: {
      whatAreApiKeys: string;
      whatAreApiKeysDesc: string;
      bookmarkletInstall: string;
      bookmarkletStep1: string;
      bookmarkletStep2: string;
      bookmarkletStep3: string;
      bookmarkletStep4: string;
      bookmarkletUsage: string;
      bookmarkletUsageStep1: string;
      bookmarkletUsageStep2: string;
      bookmarkletUsageStep3: string;
      bookmarkletUsageStep4: string;
      keyStatus: string;
      keyStatusGreen: string;
      keyStatusYellow: string;
      keyStatusRed: string;
      keyStatusGray: string;
      deleteKey: string;
      deleteKeyDesc: string;
      keyRenewalNotice: string;
      keyRenewalNoticeDesc: string;
    };
    // Orders Section
    orders: {
      whatAreOrders: string;
      whatAreOrdersDesc: string;
      singleOrder: string;
      singleOrderTitle: string;
      singleOrderStep1: string;
      singleOrderStep2: string;
      singleOrderStep3: string;
      singleOrderStep4: string;
      singleOrderStep5: string;
      batchUpload: string;
      batchUploadTitle: string;
      batchUploadStep1: string;
      batchUploadStep2: string;
      batchUploadStep3: string;
      batchUploadStep4: string;
      excelColumns: string;
      excelColumnSku: string;
      excelColumnMinPrice: string;
      excelColumnMaxPrice: string;
      excelColumnOrderType: string;
      excelColumnCurrency: string;
      editOrder: string;
      editOrderDesc: string;
      deleteOrder: string;
      deleteOrderDesc: string;
      restartOrder: string;
      restartOrderDesc: string;
    };
    // Order Details Section
    orderDetails: {
      whatIsBuybox: string;
      whatIsBuyboxDesc: string;
      orderTypes: string;
      buyboxType: string;
      buyboxTypeDesc: string;
      pressureType: string;
      pressureTypeDesc: string;
      statusIndicators: string;
      statusWorking: string;
      statusWorkingDesc: string;
      statusWaiting: string;
      statusWaitingDesc: string;
      statusInactive: string;
      statusInactiveDesc: string;
      tierLimits: string;
      tierLimitsDesc: string;
      tierDemo: string;
      tierSilver: string;
      tierGold: string;
      tierPlatinum: string;
      tierTycoon: string;
      tierMore: string;
    };
    // Contact Section
    contact: {
      title: string;
      description: string;
      phone: string;
      phoneNumber: string;
      email: string;
      emailAddress: string;
      web: string;
      webAddress: string;
    };
  };

  // About Page
  about: {
    title: string;
    backToPanel: string;
    backToLogin: string;
    whatIsPolaris: string;
    whatIsPolarisDesc: string;
    mission: string;
    missionDesc: string;
    whyPolaris: string;
    features: string[];
    contact: string;
    location: string;
    locationValue: string;
    email: string;
    copyright: string;
  };

  // Payment Page
  payment: {
    title: string;
    backToPlans: string;
    comingSoon: string;
    comingSoonDesc: string;
    selectedPlan: string;
    monthly: string;
    annual: string;
    contactInfo: string;
    paymentMethods: string;
    bankTransfer: string;
    loginToPayment: string;
    distanceSalesContract: string;
  };

  // Market Standby Page
  consensus: {
    title: string;
    subtitle: string;
    backToDashboard: string;
    whatIsConsensus: string;
    whatIsConsensusDesc: string;
    whyBetter: string;
    whyBetterIntro: string;
    whyBetterPoints: string[];
    howItWorks: string;
    howItWorksDesc: string;
    howItWorksSteps: string[];
    scoringFormula: string;
    scoringFormulaDesc: string;
    scoringFactors: string[];
    moreTime: string;
    moreTimeIntro: string;
    moreTimeTips: string[];
    upgradeTier: string;
    upgradeTierDesc: string;
    viewPlans: string;
    statusExplanation: string;
    statusExplanationDesc: string;
  };
}

export const tr: Translations = {
  // Genel
  loading: 'Yükleniyor...',
  cancel: 'İptal',
  save: 'Kaydet',
  delete: 'Sil',
  edit: 'Düzenle',
  add: 'Ekle',
  yes: 'Evet',
  no: 'Hayır',
  confirm: 'Onayla',
  select: 'Seçin',
  taglines: [
    'Rakiplerinin 450 ışık yılı önünde',
    'Yön bulmanıza yardım eder',
  ],

  // Kimlik Doğrulama
  auth: {
    signIn: 'Giriş Yap',
    signUp: 'Kayıt Ol',
    signOut: 'Çıkış Yap',
    email: 'E-posta',
    password: 'Şifre',
    confirmPassword: 'Şifre Tekrar',
    name: 'İsim',
    enterEmail: 'E-posta adresinizi girin',
    enterPassword: 'Şifrenizi girin',
    confirmYourPassword: 'Şifrenizi tekrar girin',
    enterName: 'İsminizi girin',
    signInToAccount: 'Hesabınıza giriş yapın',
    createAccount: 'Hesap oluşturun',
    noAccount: 'Hesabınız yok mu?',
    hasAccount: 'Zaten hesabınız var mı?',
    pleaseWait: 'Lütfen bekleyin...',
    nameRequired: 'İsim gereklidir',
    passwordsDoNotMatch: 'Şifreler eşleşmiyor',
    passwordRequirements: 'Şifre gereksinimleri:',
    passwordMinLength: 'En az 8 karakter',
    passwordUppercase: 'En az 1 büyük harf',
    passwordLowercase: 'En az 1 küçük harf',
    passwordNumber: 'En az 1 rakam',
    passwordSymbol: 'En az 1 sembol',
    // Şifremi unuttum
    forgotPassword: 'Şifremi Unuttum',
    forgotPasswordTitle: 'Şifre Sıfırlama',
    forgotPasswordSubtitle: 'E-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.',
    sendResetLink: 'Sıfırlama Bağlantısı Gönder',
    backToLogin: 'Giriş sayfasına dön',
    resetLinkSent: 'Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.',
    // Şifre sıfırlama
    resetPassword: 'Şifreyi Sıfırla',
    resetPasswordTitle: 'Yeni Şifre Belirleyin',
    newPassword: 'Yeni Şifre',
    enterNewPassword: 'Yeni şifrenizi girin',
    passwordResetSuccess: 'Şifreniz başarıyla sıfırlandı. Şimdi giriş yapabilirsiniz.',
    // E-posta doğrulama
    verifyEmail: 'E-posta Doğrula',
    verifyEmailTitle: 'E-posta Doğrulama',
    verifyingEmail: 'E-posta adresiniz doğrulanıyor...',
    emailVerified: 'E-posta adresiniz başarıyla doğrulandı!',
    emailVerificationFailed: 'E-posta doğrulama başarısız. Bağlantı geçersiz veya süresi dolmuş olabilir.',
    verificationEmailSent: 'Doğrulama e-postası gönderildi!',
    checkYourEmail: 'Lütfen e-posta kutunuzu kontrol edin ve doğrulama bağlantısına tıklayın. Spam/gereksiz klasörünü de kontrol etmeyi unutmayın.',
    termsNotice: 'Kayıt olarak kullanım koşullarını kabul etmiş olursunuz. Bilgileriniz KVKK standartlarının üzerinde koruma altındadır.',
    // Policy links
    termsOfUse: 'Kullanım Koşullarını',
    privacyPolicy: 'Gizlilik Politikasını',
    cookiePolicy: 'Çerez Politikasını',
    softwareDisclaimer: 'Her yazılım gibi bu yazılım da hata yapabilir. Verileriniz doğrultusunda çalışsa da istenmeyen sonuçlar ve kısa süreli teknik aksaklıklar oluşabilir.',
    termsOfUseTitle: 'Kullanım Koşulları',
    privacyPolicyTitle: 'Gizlilik Politikası',
    cookiePolicyTitle: 'Çerez Politikası',
    acceptAndRegister: 'Kabul Et ve Kaydol',
  },

  // Legal policies content
  policies: {
    terms: `<h2>Kullanım Koşulları</h2>
<p>Bu Kullanım Koşulları, polaris.polysansoft.com adresindeki İnternet sitesini kullanırken uymanız gereken kuralları içermektedir.</p>

<h3>Kapsam ve Değişiklikler</h3>
<p>polaris.polysansoft.com adresindeki İnternet sitesi, PolysanSoft tarafından işletilmektedir. Bu Kullanım Koşulları, İnternet sitesi kullanımı sırasında uygulanacak kuralları ve tarafların hak ve yükümlülüklerini belirlemektedir.</p>

<h3>Hizmet İçeriği ve Kullanım</h3>
<p>Bu İnternet sitesi tarafından sunulan hizmetler, yasal mevzuatlara uygun bir şekilde kullanılmak üzere tasarlanmıştır. Kullanıcılar, İnternet sitesini kullanırken tüm yasa, tüzük ve yönetmelikler ile uluslararası sözleşmelere ve teamüllere uymakla yükümlüdür.</p>

<h3>Kullanıcı Yükümlülükleri</h3>
<ul>
<li>Yasalara aykırı ve suç teşkil eder nitelikte paylaşımlarda bulunmamak.</li>
<li>Üçüncü kişilerin fikri mülkiyet haklarını ihlal eder nitelikte eylemlerde bulunmamak.</li>
<li>Kötü amaçlı yazılımları yaymamak veya güvenlik açıklarını istismar etmemek.</li>
<li>Sunulan hizmetleri amacına aykırı bir şekilde veya kötüye kullanmamak.</li>
</ul>

<h3>Yazılım Uyarısı</h3>
<p>Bu yazılım, kullanıcı tarafından sağlanan veriler doğrultusunda otomatik işlemler gerçekleştirmektedir. Her yazılım gibi, bu yazılım da hatalar içerebilir ve beklenmeyen sonuçlar üretebilir.</p>
<ul>
<li>Yazılım, kullanıcının sağladığı verilere dayalı olarak çalışır; yanlış veya eksik veri girişi istenmeyen sonuçlara yol açabilir.</li>
<li>Geçici teknik aksaklıklar, sistem kesintileri veya performans sorunları yaşanabilir.</li>
<li>Platform entegrasyonlarında (Trendyol, Hepsiburada vb.) üçüncü taraf kaynaklı gecikmeler veya hatalar oluşabilir.</li>
<li>Fiyat değişiklikleri ve otomasyon işlemleri geri alınamaz olabilir.</li>
</ul>
<p>PolysanSoft, yazılım kaynaklı oluşabilecek ticari kayıplardan, kar mahrumiyetinden veya dolaylı zararlardan sorumlu tutulamaz.</p>

<h3>Sorumluluk Sınırları</h3>
<p>PolysanSoft, İnternet sitesinin kullanımından doğabilecek doğrudan, dolaylı, özel veya cezai hiçbir zarardan sorumlu tutulamaz. İnternet sitemiz, "olduğu gibi" sunulmaktadır ve kesintisiz veya hatasız çalışacağına dair herhangi bir garanti verilmemektedir.</p>

<h3>Uyuşmazlık Çözümü</h3>
<p>Bu İnternet sitesinin kullanımından doğabilecek uyuşmazlıklarda, İstanbul mahkemeleri ve icra daireleri yetkili olacaktır.</p>`,

    privacy: `<h2>Gizlilik Politikası</h2>
<p>Bu İnternet sitesini kullanarak kişisel verilerinizin işlenmesini kabul etmiş olursunuz. Güvenliğiniz bizim için önemli.</p>

<h3>Veri Sorumlusu</h3>
<p>PolysanSoft, veri sorumlusu olarak, kişisel verilerinizin korunması konusunda sizleri aydınlatmayı amaçlamaktadır.</p>

<h3>Toplanan Kişisel Veriler</h3>
<ul>
<li>E-posta adresi (şifrelenmiş olarak saklanır, düz metin olarak tutulmaz)</li>
<li>Cihaz bilgileri</li>
<li>IP adresi</li>
</ul>

<h3>Şifre Güvenliği</h3>
<p>Şifreniz düz metin olarak saklanmaz. Endüstri standardı güvenlik uygulamalarına uygun olarak şifreniz hash'lenerek korunur ve orijinal haline erişim mümkün değildir.</p>

<h3>E-posta İletişimi</h3>
<p>Hizmetlerimizin sunulması kapsamında size aşağıdaki konularda e-posta gönderilebilir:</p>
<ul>
<li>Kayıt doğrulama ve hesap aktivasyonu</li>
<li>Şifre sıfırlama talepleri</li>
<li>Fatura ve ödeme bildirimleri</li>
<li>Hizmetle ilgili önemli güncellemeler ve duyurular</li>
<li>Güvenlik uyarıları</li>
</ul>
<p>Bu e-postalar hizmetin işleyişi için gerekli olup, pazarlama amaçlı değildir.</p>

<h3>Kullanılan Servisler</h3>
<p><strong>Altyapı servisleri:</strong></p>
<ul>
<li>Cloudflare: Site güvenliği ve performans optimizasyonu</li>
<li>Contabo: Altyapı ve barındırma hizmetleri</li>
</ul>

<p><strong>Ödeme servisleri:</strong></p>
<ul>
<li>Garanti BBVA: Güvenli QR ödeme ve sanal POS hizmeti</li>
<li>Havale/EFT: Banka havalesi ile ödeme seçeneği</li>
</ul>
<p>Ödeme işlemleriniz sırasında kredi kartı bilgileriniz tarafımızca saklanmaz. Tüm ödeme bilgileri Garanti BBVA tarafından PCI-DSS standartlarına uygun şekilde işlenir ve korunur.</p>

<h3>Verilerin İşlenme Amaçları</h3>
<p>Kişisel verileriniz, hizmet sunulabilmesi, yasal yükümlülüklerin yerine getirilmesi, hizmet kalitesinin artırılması ve güvenlik amaçları ile işlenmektedir.</p>

<h3>KVKK Kapsamında Haklarınız</h3>
<ul>
<li>Kişisel verilerinin işlenip işlenmediğini öğrenme</li>
<li>Kişisel verileri işlenmişse buna ilişkin bilgi talep etme</li>
<li>Kişisel verilerin işlenme amacını öğrenme</li>
<li>Kişisel verilerin eksik veya yanlış işlenmiş olması hâlinde düzeltilmesini isteme</li>
<li>Kişisel verilerin silinmesini veya yok edilmesini isteme</li>
</ul>

<h3>İletişim</h3>
<p>Kişisel verilerinizle ilgili haklarınızı kullanmak için info@polysansoft.com adresinden bizimle iletişime geçebilirsiniz.</p>`,

    cookies: `<h2>Çerez Politikası</h2>
<p>Bu metnin amacı, İnternet sitesi tarafından kullanılan çerezlerin cihazınıza yerleştirilmesi aracılığıyla elde edilen kişisel verilerin işlenmesine ilişkin sizleri bilgilendirmektir.</p>

<h3>Çerez Nedir?</h3>
<p>Çerezler, bir İnternet sayfası ziyaret edildiğinde kullanıcılara ilişkin birtakım bilgilerin kullanıcıların terminal cihazlarında depolanmasına izin veren düşük boyutlu text formatlarıdır.</p>

<h3>Çerezler Neden Kullanılır?</h3>
<ul>
<li>Oturum bilgileriniz korunur ve güvenliğiniz sağlanır.</li>
<li>Site içi deneyiminiz iyileştirilir ve hızlandırılır.</li>
<li>Tercihleriniz hatırlanarak size özel içerikler sunulur.</li>
</ul>

<h3>Kullanılan Çerez Türleri</h3>
<p><strong>Zorunlu Çerezler:</strong> İnternet sitesinin çalışması için gerekli olan çerezlerdir. Oturum yönetimi ve kimlik doğrulama için kullanılır.</p>
<p><strong>Yerel Depolama:</strong> Tercihlerinizi (dil seçimi gibi) hatırlamak için kullanılır.</p>

<h3>Çerez Yönetimi</h3>
<p>Tarayıcı ayarlarınızdan çerez tercihlerinizi istediğiniz zaman değiştirebilirsiniz. Daha fazla bilgi için info@polysansoft.com adresinden bize ulaşabilirsiniz.</p>`,
  },

  // Navigasyon
  nav: {
    dashboard: 'Panel',
    logout: 'Çıkış',
    help: 'Yardım',
    about: 'Hakkımızda',
    pricing: 'Fiyatlandırma',
  },

  // Panel
  dashboard: {
    title: 'Panel',
    products: 'Ürünler',
  },

  // Emirler
  emirler: {
    title: 'Emirler',
    addOrder: '+ Emir Ekle',
    noEmirlerMatch: 'Seçilen filtreye uygun emir bulunamadı',
    platform: 'Platform',
    product: 'Ürün',
    sku: 'SKU',
    priceRange: 'Fiyat Aralığı',
    minPrice: 'Min Fiyat',
    maxPrice: 'Max Fiyat',
    orderType: 'Emir Tipi',
    status: 'Durum',
    buyboxCapture: 'BuyBox',
    pressureApply: 'Baskı',
    working: 'Çalışıyor',
    waiting: 'Bekliyor',
    inactive: 'Devre Dışı',
    deactivationReason: 'Sebep',
    actions: 'İşlemler',
    delete: 'Sil',
    restart: 'Yeniden Başlat',
    stop: 'Durdur',
    editOrder: 'Emri Düzenle',
    orderUpdated: 'Emir başarıyla güncellendi!',
    orderUpdateError: 'Emir güncellenirken hata oluştu.',
    orderRestarted: 'Emir yeniden başlatıldı!',
    orderRestartError: 'Emir yeniden başlatılırken hata oluştu.',
    orderStopped: 'Emir durduruldu!',
    orderStopError: 'Emir durdurulurken hata oluştu.',
  },

  // API Anahtarları
  apiKeys: {
    title: 'API Anahtarları',
    addKey: '+ Anahtar Ekle',
    editKey: 'Anahtarı Düzenle',
    updateKey: 'Anahtarı Güncelle',
    version: 'Versiyon',
    selectVersion: 'Versiyon seçin',
    apiKey: 'API Anahtarı',
    noKeys: 'Henüz API anahtarı eklenmedi.',
    addFirstKey: 'Başlamak için ilk API anahtarınızı ekleyin.',
    daysLeft: 'gün kaldı',
    expired: 'Süresi Doldu',
    active: 'Aktif',
    expires: 'Bitiş:',
    added: 'Eklendi:',
    deleteConfirm: 'Bu API anahtarını silmek istediğinizden emin misiniz?',
    keyPlaceholder: 'API anahtarınızı girin',
    validFor: 'Geçerlilik:',
    days: 'gün',
    user: 'Kullanıcı',
    sellerId: 'Kullanıcı ID',
    platform: 'Platform',
    type: 'Tip',
  },

  // Filtreler
  filters: {
    all: 'Tümü',
    active: 'Aktif',
    buyboxWin: 'BuyBox Kazanan',
    pressureWinner: 'Baskı Kazanan',
    deactivated: 'Devre Dışı',
    notActivated: 'Aktif Edilmedi',
  },

  // Tablo
  table: {
    noData: 'Veri bulunamadı',
    noProductsMatch: 'Seçilen filtreye uygun ürün bulunamadı',
  },

  // Sütun başlıkları
  columns: {
    id: 'ID',
    status: 'Durum',
    name: 'Ürün Adı',
    sku: 'SKU',
    price: 'Fiyat',
    stock: 'Stok',
    deactivationReason: 'Sebep',
  },

  // Alt Bilgi
  footer: {
    aPolysanProduct: 'Bir Polysan Ürünü',
    copyright: 'Polaris',
    companyName: 'Sakıp Emre Öztürk',
    companyType: 'Genel Müdür',
    address: 'Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri',
    phone: '+90 352 606 00 24',
    email: 'info@polysansoft.com',
    privacyPolicy: 'Gizlilik Politikası',
    termsOfService: 'Kullanım Koşulları',
    returnPolicy: 'İptal ve İade',
    cookiePolicy: 'Çerez Politikası',
    dataDeletion: 'Veri Silme Talebi',
    instantDelivery: 'Anında Dijital Teslimat',
  },

  // Anahtar Ekleme Sayfası
  addKey: {
    backToDashboard: 'Panele Dön',
    title: 'Polaris Connect Kurulumu',
    subtitle: 'Mağaza verilerinizi Polaris ile senkronize etmek için aşağıdaki adımları takip edin.',
    installTitle: 'Bookmarklet\'i Yükleyin',
    dragInstruction: 'Aşağıdaki butonu yer işaretleri çubuğunuza sürükleyin',
    bookmarkletName: 'Polaris Connect',
    altInstruction: 'Veya sağ tıklayıp "Bağlantıyı yer imlerine ekle" seçeneğini kullanın',
    howToInstall: 'Kurulum Adımları',
    step1Title: 'Yer işaretleri çubuğunu görünür yapın',
    step2: 'Butonu yer işaretleri çubuğuna sürükleyin',
    step3: 'Desteklenen bir mağaza sitesine gidin',
    step4: 'Bookmarklet\'e tıklayarak çalıştırın',
    howToUse: 'Kullanım',
    useStep1: 'Mağaza hesabınıza giriş yapın',
    useStep2: 'Yer işaretleri çubuğundaki bookmarklet\'e tıklayın',
    useStep3: 'Gerekli bilgiler otomatik olarak alınacaktır',
    useStep4: 'Hata mesajı görmezseniz işlem başarılıdır. Paneli yenileyerek kontrol edebilirsiniz.',
    supportedSites: 'Desteklenen Platformlar',
    statusActive: 'Aktif',
    statusComingSoon: 'Yakında',
    importantNotice: 'Önemli Uyarı',
    removeBookmarklet: 'Polaris Connect kullandığınız sitede performansı etkileyebilir. Kullanım sonrasında sekmeyi kapatıp yeniden açmanızı tavsiye ederiz.',
    mobileNotice: 'Masaüstü Tarayıcı Gerekli',
    mobileNoticeDesc: 'Bu özellik yalnızca masaüstü tarayıcılarda çalışmaktadır. Lütfen bilgisayarınızdan erişin.',
  },

  // Emir Ekleme Sayfası
  addOrders: {
    backToDashboard: 'Panele Dön',
    title: 'Emir Ekle',
    subtitle: 'API anahtarlarınıza bağlı ürünleri listeleyip emir oluşturun.',
    selectPlatform: 'Platform Seçin',
    selectPlatformDesc: 'Ürünleri görüntülemek için yukarıdan bir platform seçin.',
    comingSoon: 'Yakında',
    searchProducts: 'Ürün Ara',
    searchPlaceholder: 'Ürün adı veya SKU ile arayın...',
    noProducts: 'Ürün Bulunamadı',
    noProductsDesc: 'Seçilen kriterlere uygun ürün bulunamadı. Lütfen API anahtarlarınızı kontrol edin.',
    loadingProducts: 'Ürünler yükleniyor...',
    addOrder: 'Emir Ekle',
    // Emir Ekleme Formu
    addOrderTitle: 'Emir Oluştur',
    productInfo: 'Ürün Bilgisi',
    priceSettings: 'Fiyat Ayarları',
    currency: 'Para Birimi',
    selectCurrency: 'Para birimi seçin',
    minPrice: 'Minimum Fiyat',
    maxPrice: 'Maksimum Fiyat',
    orderType: 'Emir Tipi',
    selectOrderType: 'Emir tipi seçin',
    buyboxCapture: 'BuyBox',
    pressureApply: 'Baskı',
    priceIncludesCommission: 'Fiyatlar komisyon dahil',
    activateCoupon: 'Kuponları Etkinleştir',
    activatePromotion: 'İndirimleri Etkinleştir',
    pricingHint: 'KDV, kargo vb. giderlerinizi hesaplayın, üstüne minimum kar payınızı ekleyin, ürününüze alıcıların ödeyeceği gerçekçi bir maksimum fiyat belirleyin ve gerisini bize bırakın.',
    // Doğrulama
    minPriceRequired: 'Minimum fiyat gereklidir',
    maxPriceRequired: 'Maksimum fiyat gereklidir',
    minPriceMustBePositive: 'Minimum fiyat 0\'dan büyük olmalıdır',
    maxPriceMustBePositive: 'Maksimum fiyat 0\'dan büyük olmalıdır',
    minPriceGreaterThanMax: 'Minimum fiyat maksimum fiyattan büyük olamaz',
    currencyRequired: 'Para birimi seçilmelidir',
    orderTypeRequired: 'Emir tipi seçilmelidir',
    // Para Birimleri
    currencyTRY: 'TRY (Türk Lirası)',
    currencyUSD: 'USD (Amerikan Doları)',
    currencyEUR: 'EUR (Euro)',
    currencyYUAN: 'CNY (Çin Yuanı)',
    // Başarı/Hata
    orderAddedSuccess: 'Emir başarıyla eklendi!',
    orderAddedError: 'Emir eklenirken bir hata oluştu.',
    // Tablo sütunları
    productName: 'Ürün Adı',
    sku: 'SKU',
    currentPrice: 'Güncel Fiyat',
    stock: 'Stok',
    actions: 'İşlemler',
    // Sayfalama
    firstPage: 'İlk sayfa',
    prevPage: 'Önceki',
    nextPage: 'Sonraki',
    lastPage: 'Son sayfa',
    products: 'ürün',
  },

  // Toplu Yükleme
  bulkUpload: {
    title: 'Excel ile Toplu Emir Ekle',
    buttonText: 'Excel ile Toplu Ekle',
    downloadTemplate: 'Şablon İndir',
    uploadFile: 'Dosya Yükle',
    templateFilename: 'emir_sablonu.xlsx',
    dropzone: 'Excel dosyasını sürükleyin veya tıklayarak seçin',
    dropzoneActive: 'Dosyayı bırakın...',
    selectFile: 'Dosya Seç',
    selectedFile: 'Seçilen dosya',
    uploadAndValidate: 'Yükle ve Doğrula',
    validating: 'Doğrulanıyor...',
    processing: 'İşleniyor... ({current}/{total})',
    resultsTitle: 'Sonuçlar',
    successCount: '{count} emir başarıyla eklendi',
    errorCount: '{count} emir hatalı',
    createdCount: '{count} yeni emir oluşturuldu',
    updatedCount: '{count} mevcut emir güncellendi',
    unchangedCount: '{count} emir değişmedi',
    actionCreated: 'Oluşturuldu',
    actionUpdated: 'Güncellendi',
    actionUnchanged: 'Değişmedi',
    downloadErrors: 'Hatalı Satırları İndir',
    close: 'Kapat',
    tryAgain: 'Tekrar Dene',
    noFile: 'Lütfen bir dosya seçin',
    invalidFormat: 'Geçersiz dosya formatı. Sadece .xlsx dosyaları kabul edilir.',
    emptyFile: 'Dosya boş veya geçersiz veri içeriyor',
    missingColumns: 'Eksik sütunlar: {columns}',
    fileTooLarge: 'Dosya boyutu çok büyük (maksimum 10MB)',
    suspiciousContent: 'Dosyada şüpheli içerik tespit edildi',
    // Şablon sütunları
    columnDescription: 'Ürün Açıklaması (opsiyonel)',
    columnBarcode: 'Barkod',
    columnMinPrice: 'Alt Fiyat',
    columnMaxPrice: 'Üst Fiyat',
    columnCurrency: 'Para Birimi (0=TRY, 1=USD, 2=EUR, 3=CNY)',
    columnOrderType: 'Emir Tipi (0=BuyBox, 1=Baskı)',
    columnCommissionIncluded: 'Komisyon Dahil (1=Evet, 0=Hayır)',
    columnCouponActive: 'Kupon Aktif (1=Evet, 0=Hayır)',
    columnPromotionActive: 'İndirim Aktif (1=Evet, 0=Hayır)',
    // Hata mesajları
    errors: {
      MISSING_BARCODE: 'Barkod eksik',
      INVALID_PRICE: 'Alt fiyat üst fiyattan büyük veya eşit olamaz',
      NEGATIVE_PRICE: 'Fiyatlar sıfırdan büyük olmalıdır',
      TIER_LIMIT_REACHED: 'Emir limitinize ulaştınız',
      DUPLICATE_ORDER: 'Bu barkod ve URL için zaten aktif emir var',
      DUPLICATE_IN_BATCH: 'Bu barkod dosyada birden fazla kez var',
      PRODUCT_NOT_FOUND: 'Ürün Trendyol\'da bulunamadı',
      PRODUCT_NOT_ON_SALE: 'Ürün satışta değil',
      PRODUCT_ARCHIVED: 'Ürün arşivlenmiş',
      PRICE_TOO_LOW: 'Alt fiyat ürün fiyatının yarısından düşük',
      PRICE_TOO_HIGH: 'Üst fiyat ürün fiyatının iki katından yüksek',
      API_AUTH_FAILED: 'Trendyol API kimlik doğrulaması başarısız',
      API_ACCESS_DENIED: 'Trendyol API erişimi reddedildi',
      API_RATE_LIMITED: 'Barkod hatalı veya geçersiz',
      INVALID_BARCODE_FORMAT: 'Barkod formatı hatalı',
      API_SERVER_ERROR: 'Trendyol sunucu hatası, tekrar deneyin',
      API_ERROR: 'Barkod hatalı veya API hatası',
      API_CONNECTION_ERROR: 'Trendyol API bağlantı hatası',
    },
  },

  // Ödeme Planları
  paymentPlans: {
    // Tooltip
    tooltipText: 'Üyelik seviyelerimizi görüntülemek için tıklayın',
    // Sayfa
    title: 'Üyelik Planları',
    subtitle: 'İşletmenize en uygun planı seçin ve hemen başlayın. Emir hakkı, mağazanızdaki toplam ürün sayısı değil; fiyat ve kampanya kontrolüne almak istediğiniz her platform için ayrı sayılan tekil barkod adedidir.',
    // Faturalama seçimi
    monthly: 'Aylık',
    annual: 'Yıllık',
    annualDiscount: '%17 tasarruf edin',
    // Seviye isimleri
    demo: 'Demo',
    tier1: 'Bronze',
    tier2: 'Bronze+',
    tier3: 'Silver',
    tier4: 'Silver+',
    tier5: 'Gold',
    tier6: 'Gold+',
    tier7: 'Platinum',
    tier8: 'Platinum+',
    tier9: 'Tycoon',
    tier10: 'Tycoon+',
    tier11: 'Daha Fazlası',
    // Fiyatlandırma
    free: 'Ücretsiz',
    perMonth: '/ay',
    perYear: '/yıl',
    tryPerMonth: '₺/ay',
    // Emir sayıları
    emirCount: 'emir',
    emirs: 'emir hakkı',
    // Seviye açıklamaları
    demoDesc: 'Kayıt olun, demo otomatik aktif olsun!',
    tier1Desc: 'Pazaryeri stratejisine ilk adımını atan girişimciler için ideal başlangıç.',
    tier2Desc: 'Operasyonlarını büyütmeye hazır, gelişen işletmeler için tasarlandı.',
    tier3Desc: 'Erişim alanını genişleten köklü satıcılar için güçlendirilmiş kapasite.',
    tier4Desc: 'Yüksek envanter talebine sahip işletmeler için sağlam altyapı.',
    tier5Desc: 'Yüksek performanslı perakende operasyonları için premium çözüm.',
    tier6Desc: 'Çok kanallı kurumsal satıcılar için gelişmiş kapasite.',
    tier7Desc: 'Büyük ölçekli pazaryeri hakimiyeti için kurumsal düzey mimari.',
    tier8Desc: 'Geniş portföye sahip sektör liderleri için kapsamlı çözüm.',
    tier9Desc: 'Pazar lideri organizasyonlar için elit performans seviyesi.',
    tier10Desc: 'Maksimum rekabet avantajı için nihai kapasite.',
    tier11Desc: 'Daha fazlası için info@polysansoft.com adresine ulaşın.',
    // Rozetler
    recommended: 'Önerilen',
    currentPlan: 'Mevcut Plan',
    trialPeriod: '1 Hafta Deneme',
    // Özellikler
    features: 'Özellikler',
    allPlatforms: 'Tüm platformlarda geçerli',
    buyboxCapture: 'BuyBox yakalama',
    pressureMode: 'Baskı modu',
    prioritySupport: 'Öncelikli destek',
    apiAccess: 'API erişimi',
    // Butonlar
    buyMonthly: 'Aylık Satın Al',
    buyAnnual: 'Yıllık Satın Al',
    contactSales: 'Satış ile İletişime Geçin',
    currentlyActive: 'Aktif Plan',
    renewMonthly: 'Aylık Uzat',
    renewAnnual: 'Yıllık Uzat',
    // Bilgi bölümleri
    whatIsEmir: 'Emir nedir?',
    whatIsEmirDesc: 'Her bir platform için her bir barkod (tekil ürün) üzerine girilen BuyBox kazanma ya da baskı yapma talimatıdır. Örneğin, Trendyol\'da 50 farklı ürün için emir girdiğinizde 50 emir hakkı kullanmış olursunuz.',
    kdvIncluded: 'Tarafınıza fatura kesilecektir. Fiyatlarımıza KDV dahildir.',
    demoTrialNote: '1 Hafta Deneme - Demo ücretsiz, Anlık maximum 1 Emir, kayıt olun otomatik aktif olsun!',
    annualBenefits: 'Yıllık Ödeme Avantajı',
    annualBenefitsDesc: '12 ay kullanım için sadece 10 aylık ödeme yapın. %17 tasarruf edin!',
    // Satış metinleri
    pitch1: 'Her ürün aynı derecede rekabet gerektirmiyor. Stratejik ürünlerinizi belirleyin, gerisini bize bırakın.',
    pitch2: 'Rakipleriniz uyurken bile BuyBox sizin olsun.',
    pitch3: '7/24 otomatik fiyat takibi ile hiçbir fırsatı kaçırmayın.',
    // SSS
    faqTitle: 'Sıkça Sorulan Sorular',
    faq1Q: 'Plan değişikliği yapabilir miyim?',
    faq1A: 'Evet, istediğiniz zaman plan değiştirebilirsiniz. Kalan bakiyeniz yeni plana aktarılır ve süreniz buna göre ayarlanır.',
    faq2Q: 'Ödeme yöntemleri nelerdir?',
    faq2A: 'Kredi kartı veya banka kartı ile güvenli ödeme yapabilirsiniz. Tüm ödemeler Garanti BBVA Sanal POS üzerinden 3D Secure doğrulaması ile işlenir. Kart bilgileriniz tarafımızda saklanmaz.',
    faq3Q: 'İptal edebilir miyim?',
    faq3A: 'Evet, istediğiniz zaman iptal edebilirsiniz. Kalan süreniz için iade yapılır.',
    // Downgrade
    showDowngrades: 'Alt planları göster',
    hideDowngrades: 'Alt planları gizle',
    downgrade: 'Alt Plana Geç',
    // Days left
    daysLeft: '{days} gün kaldı',
  },

  // Yardım Sayfası
  help: {
    title: 'Kullanım Kılavuzu',
    subtitle: 'Polaris\'i nasıl kullanacağınızı öğrenin',
    downloadPdf: 'PDF Olarak İndir',
    backToDashboard: 'Panele Dön',
    tabs: {
      gettingStarted: 'Başlangıç',
      apiKeys: 'API Anahtarları',
      orders: 'Emirler',
      orderDetails: 'Emir Detayları',
      contact: 'İletişim',
    },
    gettingStarted: {
      whatIsPolaris: 'Polaris Nedir?',
      whatIsPolarisDesc: 'Polaris, e-ticaret platformlarında fiyat optimizasyonu yaparak BuyBox kazanmanızı sağlayan otomatik bir araçtır. Ürünleriniz için belirlediğiniz fiyat aralığında en uygun fiyatı bularak pazaryerlerinde öne çıkmanıza yardımcı olur.',
      supportedPlatforms: 'Desteklenen Platformlar',
      supportedPlatformsDesc: 'Şu anda Trendyol aktif olarak desteklenmektedir. Hepsiburada, N11 ve Amazon Türkiye yakında eklenecektir.',
      createAccount: 'Hesap Oluşturma',
      createAccountStep1: 'Giriş sayfasında "Kayıt Ol" seçeneğine tıklayın',
      createAccountStep2: 'İsim, e-posta ve şifrenizi girin',
      createAccountStep3: 'Kullanım koşullarını kabul edin',
      createAccountStep4: 'E-posta adresinize gelen doğrulama linkine tıklayın',
      passwordRequirements: 'Şifre Gereksinimleri',
      passwordReq1: 'En az 8 karakter',
      passwordReq2: 'En az 1 büyük harf',
      passwordReq3: 'En az 1 küçük harf',
      passwordReq4: 'En az 1 rakam',
      passwordReq5: 'En az 1 özel karakter (!@#$%^&* vb.)',
      emailVerification: 'E-posta Doğrulama',
      emailVerificationDesc: 'Kayıt olduktan sonra e-posta adresinize bir doğrulama linki gönderilecektir. Bu linke tıklayarak hesabınızı aktive edin. Spam/gereksiz klasörünü kontrol etmeyi unutmayın.',
      login: 'Giriş Yapma',
      loginDesc: 'E-posta ve şifrenizi kullanarak giriş yapabilirsiniz. Giriş yaptıktan sonra panele yönlendirileceksiniz.',
      forgotPassword: 'Şifremi Unuttum',
      forgotPasswordDesc: 'Şifrenizi unuttuysanız, "Şifremi Unuttum" bağlantısına tıklayın. E-posta adresinize şifre sıfırlama linki gönderilecektir.',
    },
    apiKeys: {
      whatAreApiKeys: 'API Anahtarları Nedir?',
      whatAreApiKeysDesc: 'API anahtarları, Polaris\'in pazaryeri hesabınıza bağlanarak ürün ve fiyat bilgilerinize erişmesini sağlar. Her platform için ayrı bir API anahtarı eklemeniz gerekmektedir.',
      bookmarkletInstall: 'Bookmarklet Kurulumu',
      bookmarkletStep1: 'Tarayıcınızda yer işaretleri çubuğunu görünür yapın (Ctrl+Shift+B)',
      bookmarkletStep2: '"Anahtar Ekle" sayfasındaki Polaris Connect butonunu yer işaretleri çubuğuna sürükleyin',
      bookmarkletStep3: 'Desteklenen bir pazaryeri sitesine gidin ve hesabınıza giriş yapın',
      bookmarkletStep4: 'Yer işaretleri çubuğundaki Polaris Connect\'e tıklayın',
      bookmarkletUsage: 'Bookmarklet Kullanımı',
      bookmarkletUsageStep1: 'Pazaryeri hesabınıza giriş yapın (örn: Trendyol Satıcı Paneli)',
      bookmarkletUsageStep2: 'Yer işaretleri çubuğundaki Polaris Connect butonuna tıklayın',
      bookmarkletUsageStep3: 'Gerekli bilgiler otomatik olarak alınacaktır',
      bookmarkletUsageStep4: 'Hata mesajı görmezseniz işlem başarılıdır. Paneli yenileyerek kontrol edebilirsiniz.',
      keyStatus: 'Anahtar Durumu Göstergeleri',
      keyStatusGreen: 'Yeşil: Sağlıklı - 7 gün veya daha fazla geçerlilik süresi var',
      keyStatusYellow: 'Sarı: Uyarı - 6 gün veya daha az geçerlilik süresi kaldı',
      keyStatusRed: 'Kırmızı: Kritik - 2 gün veya daha az geçerlilik süresi kaldı',
      keyStatusGray: 'Gri: Süresi dolmuş',
      deleteKey: 'Anahtar Silme',
      deleteKeyDesc: 'Bir API anahtarını silmek için ilgili anahtarın yanındaki silme butonuna tıklayın. Silinen anahtarlar geri alınamaz.',
      keyRenewalNotice: 'Önemli Not',
      keyRenewalNoticeDesc: 'API anahtarları güvenlik nedeniyle 10-30 gün arasında rastgele bir sürede yenilenmesi gerekmektedir. Platform, geçerli bir API anahtarı olmadan çalışmaz. Anahtarınızı yenilemek için pazaryeri hesabınıza giriş yapın ve Polaris Connect butonuna tekrar tıklayın - sistem otomatik olarak eski anahtarı silip yenisini oluşturacaktır.',
    },
    orders: {
      whatAreOrders: 'Emirler Nedir?',
      whatAreOrdersDesc: 'Emirler, belirli bir ürün için BuyBox kazanma veya rakibe baskı yapma talimatlarıdır. Her emir, belirlediğiniz fiyat aralığında otomatik olarak en uygun fiyatı bulur.',
      singleOrder: 'Tekil Emir Ekleme',
      singleOrderTitle: 'Tek bir ürün için emir oluşturma adımları:',
      singleOrderStep1: '"Emir Ekle" sayfasına gidin ve platform seçin',
      singleOrderStep2: 'Ürün listesinden istediğiniz ürünü arayın ve seçin',
      singleOrderStep3: 'Minimum ve maksimum fiyat değerlerini girin',
      singleOrderStep4: 'Para birimi seçin (TRY, USD, EUR veya CNY)',
      singleOrderStep5: 'Emir tipini seçin (BuyBox veya Baskı) ve kaydedin',
      batchUpload: 'Toplu Excel Yüklemesi',
      batchUploadTitle: 'Birden fazla ürün için toplu emir oluşturma:',
      batchUploadStep1: '"Emir Ekle" sayfasında "Excel Şablonu İndir" butonuna tıklayın',
      batchUploadStep2: 'İndirilen şablonu doldurun',
      batchUploadStep3: 'Doldurulan Excel dosyasını yükleyin',
      batchUploadStep4: 'Sistem dosyayı doğrulayacak ve emirleri oluşturacaktır',
      excelColumns: 'Excel Şablonu Sütunları',
      excelColumnSku: 'SKU: Ürün barkodu (zorunlu)',
      excelColumnMinPrice: 'Alt Fiyat: Minimum fiyat değeri (zorunlu)',
      excelColumnMaxPrice: 'Üst Fiyat: Maksimum fiyat değeri (zorunlu)',
      excelColumnOrderType: 'Emir Tipi: 0 = BuyBox, 1 = Baskı (zorunlu)',
      excelColumnCurrency: 'Para Birimi: 0 = TRY, 1 = USD, 2 = EUR, 3 = CNY (opsiyonel, varsayılan: TRY)',
      editOrder: 'Emir Düzenleme',
      editOrderDesc: 'Paneldeki bir emri düzenlemek için emrin yanındaki düzenle butonuna tıklayın. Fiyat aralığını, para birimini ve emir tipini değiştirebilirsiniz.',
      deleteOrder: 'Emir Silme',
      deleteOrderDesc: 'Bir emri kalıcı olarak silmek için sil butonuna tıklayın. Bu işlem geri alınamaz.',
      restartOrder: 'Emir Yeniden Başlatma',
      restartOrderDesc: 'Devre dışı kalmış bir emri yeniden başlatmak için "Yeniden Başlat" butonuna tıklayın. Emir tekrar çalışmaya başlayacaktır.',
    },
    orderDetails: {
      whatIsBuybox: 'BuyBox Nedir?',
      whatIsBuyboxDesc: 'BuyBox, pazaryerlerinde bir ürün sayfasında "Sepete Ekle" butonunun yanında görünen satıcı kutusudur. BuyBox\'ı kazanan satıcı, müşterilerin büyük çoğunluğunun satın alımını gerçekleştirir. Bu nedenle BuyBox kazanmak satışlarınızı önemli ölçüde artırabilir.',
      orderTypes: 'Emir Tipleri',
      buyboxType: 'BuyBox (Tip 0)',
      buyboxTypeDesc: 'Belirlediğiniz fiyat aralığında en yüksek fiyattan BuyBox\'ı 1. sırada kazanmayı hedefler. Sistem, BuyBox\'ı kaybetmeden mümkün olan en yüksek kâr marjını bulur.',
      pressureType: 'Baskı (Tip 1)',
      pressureTypeDesc: 'Rakibe baskı uygulayarak 2. sırada bekler. Rakibin fiyatını düşürmeye zorlar ve pazar dinamiklerini gözlemler.',
      statusIndicators: 'Durum Göstergeleri',
      statusWorking: 'Çalışıyor (Yeşil)',
      statusWorkingDesc: 'Emir aktif olarak çalışıyor ve fiyat optimizasyonu yapılıyor.',
      statusWaiting: 'Bekliyor (Sarı)',
      statusWaitingDesc: 'Emir sıraya alınmış, işlenmek için bekliyor.',
      statusInactive: 'Devre Dışı (Gri)',
      statusInactiveDesc: 'Emir kullanıcı tarafından durdurulmuş veya bir hata nedeniyle devre dışı kalmış.',
      tierLimits: 'Üyelik Seviyelerine Göre Emir Limitleri',
      tierLimitsDesc: 'Her üyelik seviyesinin farklı emir limiti vardır:',
      tierDemo: 'Demo: 1 emir',
      tierSilver: 'Silver: 50 emir | Silver+: 100 emir',
      tierGold: 'Gold: 250 emir | Gold+: 500 emir',
      tierPlatinum: 'Platinum: 1.000 emir | Platinum+: 2.500 emir',
      tierTycoon: 'Tycoon: 5.000 emir | Tycoon+: 10.000 emir',
      tierMore: 'Yıllık: 10 ay öde, 12 ay kullan. Fiyatlar KDV dahildir.',
    },
    contact: {
      title: 'İletişim',
      description: 'Sorularınız veya destek talepleriniz için bizimle iletişime geçebilirsiniz.',
      phone: 'Telefon',
      phoneNumber: '+90 352 606 00 24',
      email: 'E-posta',
      emailAddress: 'info@polysansoft.com',
      web: 'Web',
      webAddress: 'polysansoft.com',
    },
  },

  // Hakkımızda Sayfası
  about: {
    title: 'Hakkımızda',
    backToPanel: 'Panel',
    backToLogin: 'Giriş Yap',
    whatIsPolaris: 'Polaris Nedir?',
    whatIsPolarisDesc: 'Polaris, e-ticaret satıcıları için geliştirilmiş profesyonel bir buybox otomasyon platformudur. Trendyol, Hepsiburada, Amazon, N11 ve Pazarama gibi önde gelen pazaryerlerinde fiyat optimizasyonu, kupon/indirim kontrolü ve rekabet takibi hizmetleri sunmaktadır.',
    mission: 'Misyonumuz',
    missionDesc: 'E-ticaret satıcılarının pazaryerlerinde rekabet avantajı elde etmelerini sağlamak. Akıllı otomasyon çözümleriyle satıcıların zamanını ve kaynaklarını daha verimli kullanmalarına yardımcı olmak.',
    whyPolaris: 'Neden Polaris?',
    features: [
      'Çoklu platform desteği (Trendyol, Hepsiburada, Amazon, N11, Pazarama)',
      '7/24 otomatik buybox takibi ve fiyat optimizasyonu',
      'Kupon ve indirim dönemlerinde akıllı fiyatlandırma',
      'Detaylı raporlama ve analiz araçları',
      'Kullanıcı dostu arayüz',
    ],
    contact: 'İletişim',
    location: 'Konum',
    locationValue: 'Kayseri, Türkiye',
    email: 'E-posta',
    copyright: '© 2025 PolysanSoft. Tüm hakları saklıdır.',
  },

  // Ödeme Sayfası
  payment: {
    title: 'Ödeme',
    backToPlans: 'Planlara Dön',
    comingSoon: 'Çok Yakında',
    comingSoonDesc: 'Online ödeme sistemi şu an geliştirme aşamasındadır. Manuel ödeme için lütfen bizimle iletişime geçin.',
    selectedPlan: 'Seçilen Plan',
    monthly: 'Aylık',
    annual: 'Yıllık',
    contactInfo: 'Ödeme işlemleri için:',
    paymentMethods: 'Desteklenen Ödeme Yöntemleri',
    bankTransfer: 'Havale / EFT',
    loginToPayment: 'Ödeme için Giriş Yap',
    distanceSalesContract: 'Mesafeli Satış Sözleşmesi',
  },

  // Pazar Bekleme Modu
  consensus: {
    title: 'Pazar Bekleme Modu',
    subtitle: 'Yoğun pazar koşullarında emirleriniz neden beklemeye alınır?',
    backToDashboard: 'Panele Dön',

    whatIsConsensus: 'Pazar Bekleme Modu Nedir?',
    whatIsConsensusDesc: 'Bazı ürünlerde pazar çok yoğun olabilir. Bu durumlarda sürekli fiyat değişikliği yapmak hem kâr marjınızı düşürür hem de fiyat istikrarını bozar. Polaris, pazar koşulları uygun olmadığında emirlerinizi geçici olarak beklemeye alır ve koşullar düzeldiğinde otomatik olarak devam eder.',

    whyBetter: 'Neden Beklemeye Alınıyor?',
    whyBetterIntro: 'Bekleme modu şu durumlarda devreye girer:',
    whyBetterPoints: [
      'Pazar çok yoğun ve fiyatlar hızla değişiyor',
      'Mevcut koşullarda fiyat değişikliği kârlı değil',
      'Sistem daha uygun bir an bekliyor',
    ],

    howItWorks: 'Ne Yapmalıyım?',
    howItWorksDesc: 'Herhangi bir işlem yapmanıza gerek yoktur:',
    howItWorksSteps: [
      'Emiriniz aktif kalmaya devam eder',
      'Pazar koşulları uygun olduğunda sistem otomatik devreye girer',
      'Bu süreçte fiyatınız mevcut seviyesinde korunur',
    ],

    scoringFormula: 'Sıkça Sorulan Sorular',
    scoringFormulaDesc: '',
    scoringFactors: [
      'Emirim iptal mi oldu? Hayır, emiriniz aktif. Sadece geçici olarak beklemede.',
      'Ne kadar sürer? Pazar koşullarına bağlıdır, genellikle kısa sürede çözülür.',
    ],

    moreTime: 'Buybox Performansınızı Artırın',
    moreTimeIntro: 'Genel öneriler:',
    moreTimeTips: [
      'Rekabetçi bir minimum fiyat belirleyin',
      'Ürün portföyünüzü çeşitlendirin',
    ],

    upgradeTier: 'Daha Fazla Bilgi',
    upgradeTierDesc: 'Üyelik planlarınız hakkında detaylı bilgi almak için planlar sayfasını ziyaret edin.',
    viewPlans: 'Planları İncele',

    statusExplanation: 'Durum Göstergesi',
    statusExplanationDesc: 'Emirlerinizde bu durum görüntülendiğinde endişelenmenize gerek yoktur. Emiriniz aktif kalmaya devam eder ve pazar koşulları uygun olduğunda otomatik olarak çalışmaya başlar.',
  },
};

export const en: Translations = {
  // Common
  loading: 'Loading...',
  cancel: 'Cancel',
  save: 'Save',
  delete: 'Delete',
  edit: 'Edit',
  add: 'Add',
  yes: 'Yes',
  no: 'No',
  confirm: 'Confirm',
  select: 'Select',
  taglines: [
    '450 light-years ahead of our competitors',
    'Helps you find your way',
  ],

  // Auth
  auth: {
    signIn: 'Sign In',
    signUp: 'Sign Up',
    signOut: 'Sign Out',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    name: 'Name',
    enterEmail: 'Enter your email',
    enterPassword: 'Enter your password',
    confirmYourPassword: 'Confirm your password',
    enterName: 'Enter your name',
    signInToAccount: 'Sign in to your account',
    createAccount: 'Create your account',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    pleaseWait: 'Please wait...',
    nameRequired: 'Name is required',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordRequirements: 'Password requirements:',
    passwordMinLength: 'At least 8 characters',
    passwordUppercase: 'At least 1 uppercase letter',
    passwordLowercase: 'At least 1 lowercase letter',
    passwordNumber: 'At least 1 number',
    passwordSymbol: 'At least 1 symbol',
    // Forgot password
    forgotPassword: 'Forgot Password',
    forgotPasswordTitle: 'Reset Password',
    forgotPasswordSubtitle: 'Enter your email address and we will send you a password reset link.',
    sendResetLink: 'Send Reset Link',
    backToLogin: 'Back to login',
    resetLinkSent: 'Password reset link has been sent to your email.',
    // Reset password
    resetPassword: 'Reset Password',
    resetPasswordTitle: 'Set New Password',
    newPassword: 'New Password',
    enterNewPassword: 'Enter your new password',
    passwordResetSuccess: 'Your password has been reset successfully. You can now sign in.',
    // Email verification
    verifyEmail: 'Verify Email',
    verifyEmailTitle: 'Email Verification',
    verifyingEmail: 'Verifying your email address...',
    emailVerified: 'Your email has been verified successfully!',
    emailVerificationFailed: 'Email verification failed. The link may be invalid or expired.',
    verificationEmailSent: 'Verification email sent!',
    checkYourEmail: 'Please check your inbox and click the verification link. Don\'t forget to check your spam/junk folder.',
    termsNotice: 'By registering, you accept our terms and conditions. Your information is protected beyond KVKK standards.',
    // Policy links
    termsOfUse: 'Terms of Use',
    privacyPolicy: 'Privacy Policy',
    cookiePolicy: 'Cookie Policy',
    softwareDisclaimer: 'Like any software, this software may contain errors. Even when operating with your data, unexpected results and temporary technical issues may occur.',
    termsOfUseTitle: 'Terms of Use',
    privacyPolicyTitle: 'Privacy Policy',
    cookiePolicyTitle: 'Cookie Policy',
    acceptAndRegister: 'Accept and Register',
  },

  // Legal policies content
  policies: {
    terms: `<h2>Terms of Use</h2>
<p>These Terms of Use contain the rules you must follow when using the website at polaris.polysansoft.com.</p>

<h3>Scope and Changes</h3>
<p>The website at polaris.polysansoft.com is operated by PolysanSoft. These Terms of Use determine the rules to be applied during the use of the website and the rights and obligations of the parties.</p>

<h3>Service Content and Usage</h3>
<p>The services offered by this website are designed to be used in accordance with legal regulations. Users are obliged to comply with all laws, regulations and international agreements when using the website.</p>

<h3>User Obligations</h3>
<ul>
<li>Not to share content that is illegal or constitutes a crime.</li>
<li>Not to engage in activities that violate intellectual property rights of third parties.</li>
<li>Not to spread malicious software or exploit security vulnerabilities.</li>
<li>Not to misuse or abuse the services offered.</li>
</ul>

<h3>Software Disclaimer</h3>
<p>This software performs automated operations based on data provided by the user. Like any software, this software may contain errors and produce unexpected results.</p>
<ul>
<li>The software operates based on user-provided data; incorrect or incomplete data entry may lead to undesired results.</li>
<li>Temporary technical issues, system interruptions, or performance problems may occur.</li>
<li>Third-party delays or errors may occur in platform integrations (Trendyol, Hepsiburada, etc.).</li>
<li>Price changes and automation operations may be irreversible.</li>
</ul>
<p>PolysanSoft cannot be held responsible for commercial losses, loss of profit, or indirect damages that may arise from software-related issues.</p>

<h3>Limitation of Liability</h3>
<p>PolysanSoft cannot be held responsible for any direct, indirect, special, or punitive damages arising from the use of the website. Our website is provided "as is" and no guarantee is given that it will operate without interruption or error.</p>

<h3>Dispute Resolution</h3>
<p>Istanbul courts and enforcement offices shall have jurisdiction over disputes arising from the use of this website.</p>`,

    privacy: `<h2>Privacy Policy</h2>
<p>By using this website, you accept the processing of your personal data. Your security is important to us.</p>

<h3>Data Controller</h3>
<p>PolysanSoft, as data controller, aims to inform you about the protection of your personal data.</p>

<h3>Collected Personal Data</h3>
<ul>
<li>Email address (stored encrypted, not kept in plain text)</li>
<li>Device information</li>
<li>IP address</li>
</ul>

<h3>Password Security</h3>
<p>Your password is not stored in plain text. In accordance with industry-standard security practices, your password is protected by hashing and the original form is inaccessible.</p>

<h3>Email Communication</h3>
<p>As part of providing our services, we may send you emails regarding:</p>
<ul>
<li>Registration verification and account activation</li>
<li>Password reset requests</li>
<li>Invoice and payment notifications</li>
<li>Important service updates and announcements</li>
<li>Security alerts</li>
</ul>
<p>These emails are necessary for the operation of the service and are not for marketing purposes.</p>

<h3>Services Used</h3>
<p><strong>Infrastructure services:</strong></p>
<ul>
<li>Cloudflare: Site security and performance optimization</li>
<li>Contabo: Infrastructure and hosting services</li>
</ul>

<p><strong>Payment services:</strong></p>
<ul>
<li>Garanti BBVA: Secure QR payment and virtual POS service</li>
<li>Bank Transfer/Wire: Bank transfer payment option</li>
</ul>
<p>Your credit card information is not stored by us during payment transactions. All payment information is processed and protected by Garanti BBVA in compliance with PCI-DSS standards.</p>

<h3>Purposes of Data Processing</h3>
<p>Your personal data is processed for the purpose of providing services, fulfilling legal obligations, improving service quality, and security purposes.</p>

<h3>Your Rights Under KVKK</h3>
<ul>
<li>Learning whether your personal data is processed</li>
<li>Requesting information if personal data has been processed</li>
<li>Learning the purpose of personal data processing</li>
<li>Requesting correction if personal data is incomplete or incorrect</li>
<li>Requesting deletion or destruction of personal data</li>
</ul>

<h3>Contact</h3>
<p>To exercise your rights regarding your personal data, you can contact us at info@polysansoft.com.</p>`,

    cookies: `<h2>Cookie Policy</h2>
<p>The purpose of this text is to inform you about the processing of personal data obtained through the placement of cookies used by the website on your device.</p>

<h3>What is a Cookie?</h3>
<p>Cookies are small text formats that allow certain information about users to be stored on users' terminal devices when visiting a web page.</p>

<h3>Why Are Cookies Used?</h3>
<ul>
<li>Your session information is protected and your security is ensured.</li>
<li>Your on-site experience is improved and accelerated.</li>
<li>Your preferences are remembered and personalized content is presented to you.</li>
</ul>

<h3>Types of Cookies Used</h3>
<p><strong>Essential Cookies:</strong> Cookies that are necessary for the website to function. Used for session management and authentication.</p>
<p><strong>Local Storage:</strong> Used to remember your preferences (such as language selection).</p>

<h3>Cookie Management</h3>
<p>You can change your cookie preferences at any time from your browser settings. For more information, you can contact us at info@polysansoft.com.</p>`,
  },

  // Navigation
  nav: {
    dashboard: 'Dashboard',
    logout: 'Logout',
    help: 'Help',
    about: 'About',
    pricing: 'Pricing',
  },

  // Dashboard
  dashboard: {
    title: 'Dashboard',
    products: 'Products',
  },

  // Emirler (Orders)
  emirler: {
    title: 'Orders',
    addOrder: '+ Add Order',
    noEmirlerMatch: 'No orders match the selected filter',
    platform: 'Platform',
    product: 'Product',
    sku: 'SKU',
    priceRange: 'Price Range',
    minPrice: 'Min Price',
    maxPrice: 'Max Price',
    orderType: 'Order Type',
    status: 'Status',
    buyboxCapture: 'BuyBox',
    pressureApply: 'Pressure',
    working: 'Working',
    waiting: 'Waiting',
    inactive: 'Inactive',
    deactivationReason: 'Reason',
    actions: 'Actions',
    delete: 'Delete',
    restart: 'Restart',
    stop: 'Stop',
    editOrder: 'Edit Order',
    orderUpdated: 'Order updated successfully!',
    orderUpdateError: 'Failed to update order.',
    orderRestarted: 'Order restarted!',
    orderRestartError: 'Failed to restart order.',
    orderStopped: 'Order stopped!',
    orderStopError: 'Failed to stop order.',
  },

  // API Keys
  apiKeys: {
    title: 'API Keys',
    addKey: '+ Add Key',
    editKey: 'Edit Key',
    updateKey: 'Update Key',
    version: 'Version',
    selectVersion: 'Select version',
    apiKey: 'API Key',
    noKeys: 'No API keys added yet.',
    addFirstKey: 'Add your first API key to get started.',
    daysLeft: 'd left',
    expired: 'Expired',
    active: 'Active',
    expires: 'Expires:',
    added: 'Added:',
    deleteConfirm: 'Are you sure you want to delete this API key?',
    keyPlaceholder: 'Enter your API key',
    validFor: 'Valid for:',
    days: 'days',
    user: 'User',
    sellerId: 'Merchant ID',
    platform: 'Platform',
    type: 'Type',
  },

  // Filters
  filters: {
    all: 'All',
    active: 'Active',
    buyboxWin: 'BuyBox Win',
    pressureWinner: 'Pressure Winner',
    deactivated: 'Deactivated',
    notActivated: 'Not Activated',
  },

  // Table
  table: {
    noData: 'No data available',
    noProductsMatch: 'No products match the selected filter',
  },

  // Column headers
  columns: {
    id: 'ID',
    status: 'Status',
    name: 'Product Name',
    sku: 'SKU',
    price: 'Price',
    stock: 'Stock',
    deactivationReason: 'Reason',
  },

  // Footer
  footer: {
    aPolysanProduct: 'A Polysan Product',
    copyright: 'Polaris',
    companyName: 'Sakıp Emre Öztürk',
    companyType: 'General Manager',
    address: 'Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri',
    phone: '+90 352 606 00 24',
    email: 'info@polysansoft.com',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    returnPolicy: 'Cancellation & Refund',
    cookiePolicy: 'Cookie Policy',
    dataDeletion: 'Data Deletion Request',
    instantDelivery: 'Instant Digital Delivery',
  },

  // Add Key Page
  addKey: {
    backToDashboard: 'Back to Dashboard',
    title: 'Polaris Connect Setup',
    subtitle: 'Follow the steps below to sync your store data with Polaris.',
    installTitle: 'Install the Bookmarklet',
    dragInstruction: 'Drag the button below to your bookmarks bar',
    bookmarkletName: 'Polaris Connect',
    altInstruction: 'Or right-click and select "Bookmark this link"',
    howToInstall: 'Installation Steps',
    step1Title: 'Enable the bookmarks bar',
    step2: 'Drag the button to your bookmarks bar',
    step3: 'Navigate to a supported store site',
    step4: 'Click the bookmarklet to run it',
    howToUse: 'Usage',
    useStep1: 'Log in to your store account',
    useStep2: 'Click the bookmarklet in your bookmarks bar',
    useStep3: 'Required information will be collected automatically',
    useStep4: 'No error means success. Refresh the dashboard to see your added keys.',
    supportedSites: 'Supported Platforms',
    statusActive: 'Active',
    statusComingSoon: 'Coming Soon',
    importantNotice: 'Important Notice',
    removeBookmarklet: 'Polaris Connect may affect performance on the site you are using. We recommend closing and reopening the tab after use.',
    mobileNotice: 'Desktop Browser Required',
    mobileNoticeDesc: 'This feature only works on desktop browsers. Please access from your computer.',
  },

  // Add Orders Page
  addOrders: {
    backToDashboard: 'Back to Dashboard',
    title: 'Add Order',
    subtitle: 'List products from your API keys and create orders.',
    selectPlatform: 'Select Platform',
    selectPlatformDesc: 'Select a platform above to view products.',
    comingSoon: 'Coming Soon',
    searchProducts: 'Search Products',
    searchPlaceholder: 'Search by product name or SKU...',
    noProducts: 'No Products Found',
    noProductsDesc: 'No products match the selected criteria. Please check your API keys.',
    loadingProducts: 'Loading products...',
    addOrder: 'Add Order',
    // Add Order Form
    addOrderTitle: 'Create Order',
    productInfo: 'Product Information',
    priceSettings: 'Price Settings',
    currency: 'Currency',
    selectCurrency: 'Select currency',
    minPrice: 'Minimum Price',
    maxPrice: 'Maximum Price',
    orderType: 'Order Type',
    selectOrderType: 'Select order type',
    buyboxCapture: 'BuyBox',
    pressureApply: 'Pressure',
    priceIncludesCommission: 'Prices include commission',
    activateCoupon: 'Enable Coupons',
    activatePromotion: 'Enable Promotions',
    pricingHint: 'Calculate your VAT, shipping, and other expenses, add your minimum profit margin, set a realistic maximum price that buyers will pay for your product, and leave the rest to us.',
    // Validation
    minPriceRequired: 'Minimum price is required',
    maxPriceRequired: 'Maximum price is required',
    minPriceMustBePositive: 'Minimum price must be greater than 0',
    maxPriceMustBePositive: 'Maximum price must be greater than 0',
    minPriceGreaterThanMax: 'Minimum price cannot be greater than maximum price',
    currencyRequired: 'Currency is required',
    orderTypeRequired: 'Order type is required',
    // Currencies
    currencyTRY: 'TRY (Turkish Lira)',
    currencyUSD: 'USD (US Dollar)',
    currencyEUR: 'EUR (Euro)',
    currencyYUAN: 'CNY (Chinese Yuan)',
    // Success/Error
    orderAddedSuccess: 'Order added successfully!',
    orderAddedError: 'An error occurred while adding the order.',
    // Table columns
    productName: 'Product Name',
    sku: 'SKU',
    currentPrice: 'Current Price',
    stock: 'Stock',
    actions: 'Actions',
    // Pagination
    firstPage: 'First page',
    prevPage: 'Previous',
    nextPage: 'Next',
    lastPage: 'Last page',
    products: 'products',
  },

  // Bulk Upload
  bulkUpload: {
    title: 'Bulk Add Orders via Excel',
    buttonText: 'Bulk Add via Excel',
    downloadTemplate: 'Download Template',
    uploadFile: 'Upload File',
    templateFilename: 'order_template.xlsx',
    dropzone: 'Drag and drop Excel file or click to select',
    dropzoneActive: 'Drop the file...',
    selectFile: 'Select File',
    selectedFile: 'Selected file',
    uploadAndValidate: 'Upload and Validate',
    validating: 'Validating...',
    processing: 'Processing... ({current}/{total})',
    resultsTitle: 'Results',
    successCount: '{count} orders added successfully',
    errorCount: '{count} orders failed',
    createdCount: '{count} new orders created',
    updatedCount: '{count} existing orders updated',
    unchangedCount: '{count} orders unchanged',
    actionCreated: 'Created',
    actionUpdated: 'Updated',
    actionUnchanged: 'Unchanged',
    downloadErrors: 'Download Failed Rows',
    close: 'Close',
    tryAgain: 'Try Again',
    noFile: 'Please select a file',
    invalidFormat: 'Invalid file format. Only .xlsx files are accepted.',
    emptyFile: 'File is empty or contains invalid data',
    missingColumns: 'Missing columns: {columns}',
    fileTooLarge: 'File size too large (maximum 10MB)',
    suspiciousContent: 'Suspicious content detected in file',
    // Template columns
    columnDescription: 'Product Description (optional)',
    columnBarcode: 'Barcode',
    columnMinPrice: 'Min Price',
    columnMaxPrice: 'Max Price',
    columnCurrency: 'Currency (0=TRY, 1=USD, 2=EUR, 3=CNY)',
    columnOrderType: 'Order Type (0=BuyBox, 1=Pressure)',
    columnCommissionIncluded: 'Commission Included (1=Yes, 0=No)',
    columnCouponActive: 'Coupon Active (1=Yes, 0=No)',
    columnPromotionActive: 'Promotion Active (1=Yes, 0=No)',
    // Error messages
    errors: {
      MISSING_BARCODE: 'Barcode is missing',
      INVALID_PRICE: 'Min price cannot be greater than or equal to max price',
      NEGATIVE_PRICE: 'Prices must be greater than zero',
      TIER_LIMIT_REACHED: 'You have reached your order limit',
      DUPLICATE_ORDER: 'An active order already exists for this barcode and URL',
      DUPLICATE_IN_BATCH: 'This barcode appears multiple times in the file',
      PRODUCT_NOT_FOUND: 'Product not found on Trendyol',
      PRODUCT_NOT_ON_SALE: 'Product is not on sale',
      PRODUCT_ARCHIVED: 'Product is archived',
      PRICE_TOO_LOW: 'Min price is less than half of the product price',
      PRICE_TOO_HIGH: 'Max price is more than double the product price',
      API_AUTH_FAILED: 'Trendyol API authentication failed',
      API_ACCESS_DENIED: 'Trendyol API access denied',
      API_RATE_LIMITED: 'Invalid or unsupported barcode',
      INVALID_BARCODE_FORMAT: 'Invalid barcode format',
      API_SERVER_ERROR: 'Trendyol server error, please retry',
      API_ERROR: 'Invalid barcode or API error',
      API_CONNECTION_ERROR: 'Trendyol API connection error',
    },
  },

  // Payment Plans
  paymentPlans: {
    // Tooltip
    tooltipText: 'Click to view our membership plans',
    // Page
    title: 'Membership Plans',
    subtitle: 'Choose the plan that fits your business and get started. Order credits refer to unique barcodes you want to control for pricing and campaigns, counted separately per platform, not your total product count.',
    // Billing toggle
    monthly: 'Monthly',
    annual: 'Annual',
    annualDiscount: 'Save 17%',
    // Tier names
    demo: 'Demo',
    tier1: 'Bronze',
    tier2: 'Bronze+',
    tier3: 'Silver',
    tier4: 'Silver+',
    tier5: 'Gold',
    tier6: 'Gold+',
    tier7: 'Platinum',
    tier8: 'Platinum+',
    tier9: 'Tycoon',
    tier10: 'Tycoon+',
    tier11: 'Need More?',
    // Pricing
    free: 'Free',
    perMonth: '/mo',
    perYear: '/yr',
    tryPerMonth: '₺/mo',
    // Emir counts
    emirCount: 'order',
    emirs: 'orders',
    // Tier descriptions
    demoDesc: 'Just register and demo activates automatically!',
    tier1Desc: 'Perfect for entrepreneurs launching their first marketplace strategy.',
    tier2Desc: 'Designed for growing businesses ready to scale their operations.',
    tier3Desc: 'Enhanced capabilities for established sellers expanding their reach.',
    tier4Desc: 'Robust infrastructure for businesses with substantial inventory demands.',
    tier5Desc: 'Premium solution engineered for high-performance retail operations.',
    tier6Desc: 'Advanced capacity tailored for multi-channel enterprise sellers.',
    tier7Desc: 'Enterprise-grade architecture for large-scale marketplace dominance.',
    tier8Desc: 'Comprehensive solution for industry leaders with extensive portfolios.',
    tier9Desc: 'Elite performance tier for market-leading organizations.',
    tier10Desc: 'Ultimate capacity designed for maximum competitive advantage.',
    tier11Desc: 'For more, contact info@polysansoft.com.',
    // Badges
    recommended: 'Recommended',
    currentPlan: 'Current Plan',
    trialPeriod: '1 Week Trial',
    // Features
    features: 'Features',
    allPlatforms: 'Works on all platforms',
    buyboxCapture: 'BuyBox capture',
    pressureMode: 'Pressure mode',
    prioritySupport: 'Priority support',
    apiAccess: 'API access',
    // Buttons
    buyMonthly: 'Buy Monthly',
    buyAnnual: 'Buy Annual',
    contactSales: 'Contact Sales',
    currentlyActive: 'Active Plan',
    renewMonthly: 'Extend Monthly',
    renewAnnual: 'Extend Annually',
    // Info sections
    whatIsEmir: 'What is an order?',
    whatIsEmirDesc: 'An order is a BuyBox capture or pressure instruction entered for each unique product (barcode) on each platform. For example, entering orders for 50 different products on Trendyol uses 50 order credits.',
    kdvIncluded: 'You will receive an invoice. VAT is included in our prices.',
    demoTrialNote: '1-Week Trial - Free Demo, max 1 active Emir at a time, just register and it activates automatically!',
    annualBenefits: 'Annual Payment Benefit',
    annualBenefitsDesc: 'Pay for only 10 months and get 12 months of service. Save 17%!',
    // Sales pitches
    pitch1: 'Not every product requires the same level of competition. Identify your strategic products, leave the rest to us.',
    pitch2: 'Own the BuyBox even while your competitors sleep.',
    pitch3: 'Never miss an opportunity with 24/7 automatic price monitoring.',
    // FAQ
    faqTitle: 'Frequently Asked Questions',
    faq1Q: 'Can I change my plan?',
    faq1A: 'Yes, you can switch plans at any time. Your remaining balance transfers to the new plan and your subscription time adjusts accordingly.',
    faq2Q: 'What payment methods are accepted?',
    faq2A: 'You can pay securely with credit or debit card. All payments are processed via Garanti BBVA Virtual POS with 3D Secure verification. Your card information is not stored by us.',
    faq3Q: 'Can I cancel?',
    faq3A: 'Yes, you can cancel anytime. The remaining time will be refunded.',
    // Downgrade
    showDowngrades: 'Show lower plans',
    hideDowngrades: 'Hide lower plans',
    downgrade: 'Downgrade',
    // Days left
    daysLeft: '{days} days left',
  },

  // Help Page
  help: {
    title: 'User Guide',
    subtitle: 'Learn how to use Polaris',
    downloadPdf: 'Download as PDF',
    backToDashboard: 'Back to Dashboard',
    tabs: {
      gettingStarted: 'Getting Started',
      apiKeys: 'API Keys',
      orders: 'Orders',
      orderDetails: 'Order Details',
      contact: 'Contact',
    },
    gettingStarted: {
      whatIsPolaris: 'What is Polaris?',
      whatIsPolarisDesc: 'Polaris is an automated tool that helps you win the BuyBox by optimizing prices on e-commerce platforms. It finds the optimal price within your specified range to help you stand out in marketplaces.',
      supportedPlatforms: 'Supported Platforms',
      supportedPlatformsDesc: 'Currently, Trendyol is actively supported. Hepsiburada, N11, and Amazon Turkey will be added soon.',
      createAccount: 'Creating an Account',
      createAccountStep1: 'Click "Sign Up" on the login page',
      createAccountStep2: 'Enter your name, email, and password',
      createAccountStep3: 'Accept the terms and conditions',
      createAccountStep4: 'Click the verification link sent to your email',
      passwordRequirements: 'Password Requirements',
      passwordReq1: 'At least 8 characters',
      passwordReq2: 'At least 1 uppercase letter',
      passwordReq3: 'At least 1 lowercase letter',
      passwordReq4: 'At least 1 number',
      passwordReq5: 'At least 1 special character (!@#$%^&* etc.)',
      emailVerification: 'Email Verification',
      emailVerificationDesc: 'After registration, a verification link will be sent to your email. Click this link to activate your account. Don\'t forget to check your spam/junk folder.',
      login: 'Logging In',
      loginDesc: 'You can log in using your email and password. After logging in, you will be redirected to the dashboard.',
      forgotPassword: 'Forgot Password',
      forgotPasswordDesc: 'If you forgot your password, click the "Forgot Password" link. A password reset link will be sent to your email.',
    },
    apiKeys: {
      whatAreApiKeys: 'What are API Keys?',
      whatAreApiKeysDesc: 'API keys allow Polaris to connect to your marketplace account to access your product and pricing information. You need to add a separate API key for each platform.',
      bookmarkletInstall: 'Bookmarklet Installation',
      bookmarkletStep1: 'Make the bookmarks bar visible in your browser (Ctrl+Shift+B)',
      bookmarkletStep2: 'Drag the Polaris Connect button from the "Add Key" page to your bookmarks bar',
      bookmarkletStep3: 'Navigate to a supported marketplace and log in to your account',
      bookmarkletStep4: 'Click Polaris Connect in your bookmarks bar',
      bookmarkletUsage: 'Bookmarklet Usage',
      bookmarkletUsageStep1: 'Log in to your marketplace account (e.g., Trendyol Seller Panel)',
      bookmarkletUsageStep2: 'Click the Polaris Connect button in your bookmarks bar',
      bookmarkletUsageStep3: 'Required information will be collected automatically',
      bookmarkletUsageStep4: 'No error message means success. Refresh the dashboard to verify.',
      keyStatus: 'Key Status Indicators',
      keyStatusGreen: 'Green: Healthy - 7 days or more remaining',
      keyStatusYellow: 'Yellow: Warning - 6 days or less remaining',
      keyStatusRed: 'Red: Critical - 2 days or less remaining',
      keyStatusGray: 'Gray: Expired',
      deleteKey: 'Deleting a Key',
      deleteKeyDesc: 'To delete an API key, click the delete button next to the key. Deleted keys cannot be recovered.',
      keyRenewalNotice: 'Important Note',
      keyRenewalNoticeDesc: 'API keys need to be renewed every 10-30 days (randomly assigned) for security reasons. The platform will not work without a valid API key. To renew your key, log in to your marketplace account and click the Polaris Connect button again - the system will automatically delete the old key and create a new one.',
    },
    orders: {
      whatAreOrders: 'What are Orders?',
      whatAreOrdersDesc: 'Orders are instructions for winning the BuyBox or applying pressure on competitors for a specific product. Each order automatically finds the optimal price within your specified range.',
      singleOrder: 'Adding a Single Order',
      singleOrderTitle: 'Steps to create an order for a single product:',
      singleOrderStep1: 'Go to the "Add Order" page and select a platform',
      singleOrderStep2: 'Search for and select the product you want from the list',
      singleOrderStep3: 'Enter the minimum and maximum price values',
      singleOrderStep4: 'Select the currency (TRY, USD, EUR, or CNY)',
      singleOrderStep5: 'Select the order type (BuyBox or Pressure) and save',
      batchUpload: 'Batch Excel Upload',
      batchUploadTitle: 'Creating multiple orders at once:',
      batchUploadStep1: 'Click "Download Excel Template" on the "Add Order" page',
      batchUploadStep2: 'Fill in the downloaded template',
      batchUploadStep3: 'Upload the completed Excel file',
      batchUploadStep4: 'The system will validate the file and create the orders',
      excelColumns: 'Excel Template Columns',
      excelColumnSku: 'SKU: Product barcode (required)',
      excelColumnMinPrice: 'Min Price: Minimum price value (required)',
      excelColumnMaxPrice: 'Max Price: Maximum price value (required)',
      excelColumnOrderType: 'Order Type: 0 = BuyBox, 1 = Pressure (required)',
      excelColumnCurrency: 'Currency: 0 = TRY, 1 = USD, 2 = EUR, 3 = CNY (optional, default: TRY)',
      editOrder: 'Editing an Order',
      editOrderDesc: 'To edit an order, click the edit button next to it. You can change the price range, currency, and order type.',
      deleteOrder: 'Deleting an Order',
      deleteOrderDesc: 'To permanently delete an order, click the delete button. This action cannot be undone.',
      restartOrder: 'Restarting an Order',
      restartOrderDesc: 'To restart an inactive order, click the "Restart" button. The order will start working again.',
    },
    orderDetails: {
      whatIsBuybox: 'What is BuyBox?',
      whatIsBuyboxDesc: 'BuyBox is the seller box that appears next to the "Add to Cart" button on a product page in marketplaces. The seller who wins the BuyBox receives the majority of customer purchases. Winning the BuyBox can significantly increase your sales.',
      orderTypes: 'Order Types',
      buyboxType: 'BuyBox (Type 0)',
      buyboxTypeDesc: 'Aims to win the BuyBox in 1st position at the highest price within your range. The system finds the highest profit margin possible without losing the BuyBox.',
      pressureType: 'Pressure (Type 1)',
      pressureTypeDesc: 'Waits in 2nd position by applying pressure on competitors. Forces competitors to lower their prices and monitors market dynamics.',
      statusIndicators: 'Status Indicators',
      statusWorking: 'Working (Green)',
      statusWorkingDesc: 'The order is actively running and price optimization is being performed.',
      statusWaiting: 'Waiting (Yellow)',
      statusWaitingDesc: 'The order is queued and waiting to be processed.',
      statusInactive: 'Inactive (Gray)',
      statusInactiveDesc: 'The order has been stopped by the user or disabled due to an error.',
      tierLimits: 'Order Limits by Membership Tier',
      tierLimitsDesc: 'Each membership tier has different order limits:',
      tierDemo: 'Demo: 1 order',
      tierSilver: 'Silver: 50 orders | Silver+: 100 orders',
      tierGold: 'Gold: 250 orders | Gold+: 500 orders',
      tierPlatinum: 'Platinum: 1,000 orders | Platinum+: 2,500 orders',
      tierTycoon: 'Tycoon: 5,000 orders | Tycoon+: 10,000 orders',
      tierMore: 'Annual: pay 10 months, get 12. All prices include VAT.',
    },
    contact: {
      title: 'Contact',
      description: 'For questions or support requests, you can contact us.',
      phone: 'Phone',
      phoneNumber: '+90 352 606 00 24',
      email: 'Email',
      emailAddress: 'info@polysansoft.com',
      web: 'Web',
      webAddress: 'polysansoft.com',
    },
  },

  // About Page
  about: {
    title: 'About Us',
    backToPanel: 'Dashboard',
    backToLogin: 'Sign In',
    whatIsPolaris: 'What is Polaris?',
    whatIsPolarisDesc: 'Polaris is a professional buybox automation platform designed for e-commerce sellers. It provides price optimization, coupon/discount tracking, and competition monitoring services on leading marketplaces like Trendyol, Hepsiburada, Amazon, N11, and Pazarama.',
    mission: 'Our Mission',
    missionDesc: 'To help e-commerce sellers gain competitive advantage in marketplaces. To assist sellers in using their time and resources more efficiently with smart automation solutions.',
    whyPolaris: 'Why Polaris?',
    features: [
      'Multi-platform support (Trendyol, Hepsiburada, Amazon, N11, Pazarama)',
      '24/7 automatic buybox tracking and price optimization',
      'Smart pricing during coupon and discount periods',
      'Detailed reporting and analytics tools',
      'User-friendly interface',
    ],
    contact: 'Contact',
    location: 'Location',
    locationValue: 'Kayseri, Turkey',
    email: 'Email',
    copyright: '© 2025 PolysanSoft. All rights reserved.',
  },

  // Payment Page
  payment: {
    title: 'Payment',
    backToPlans: 'Back to Plans',
    comingSoon: 'Coming Soon',
    comingSoonDesc: 'The online payment system is currently under development. Please contact us for manual payment.',
    selectedPlan: 'Selected Plan',
    monthly: 'Monthly',
    annual: 'Annual',
    contactInfo: 'For payment inquiries:',
    paymentMethods: 'Supported Payment Methods',
    bankTransfer: 'Bank Transfer / Wire',
    loginToPayment: 'Login to Pay',
    distanceSalesContract: 'Distance Sales Agreement',
  },

  // Market Standby
  consensus: {
    title: 'Market Standby',
    subtitle: 'Why are your orders placed on standby during busy market conditions?',
    backToDashboard: 'Back to Dashboard',

    whatIsConsensus: 'What is Market Standby?',
    whatIsConsensusDesc: 'Some products experience very high market activity. In these conditions, constantly adjusting prices can hurt your profit margins and cause price instability. Polaris temporarily places your orders on standby when market conditions are unfavorable and automatically resumes when conditions improve.',

    whyBetter: 'Why Is It On Standby?',
    whyBetterIntro: 'Standby mode activates when:',
    whyBetterPoints: [
      'The market is very active and prices are changing rapidly',
      'Price adjustments would not be profitable under current conditions',
      'The system is waiting for a better moment',
    ],

    howItWorks: 'What Should I Do?',
    howItWorksDesc: 'No action is needed on your part:',
    howItWorksSteps: [
      'Your order remains active',
      'The system automatically resumes when market conditions improve',
      'Your price is maintained at its current level during this period',
    ],

    scoringFormula: 'Frequently Asked Questions',
    scoringFormulaDesc: '',
    scoringFactors: [
      'Is my order cancelled? No, your order is active. It is just temporarily on standby.',
      'How long does it last? It depends on market conditions, usually resolves quickly.',
    ],

    moreTime: 'Improve Your Buybox Performance',
    moreTimeIntro: 'General recommendations:',
    moreTimeTips: [
      'Set a competitive minimum price',
      'Diversify your product portfolio',
    ],

    upgradeTier: 'Learn More',
    upgradeTierDesc: 'Visit the plans page for detailed information about membership options.',
    viewPlans: 'View Plans',

    statusExplanation: 'Status Indicator',
    statusExplanationDesc: 'When this status appears on your orders, there is nothing to worry about. Your order remains active and will automatically resume when market conditions are favorable.',
  },
};

export const translations: Record<Language, Translations> = { tr, en };
